import React, { useState, useCallback, useEffect, useMemo, useRef, isValidElement, cloneElement } from 'react';
import { useAuthNavigation } from '../shared/navigation/useAuthNavigation';
import { View, Text, StyleSheet, RefreshControl, AppState, useWindowDimensions, type AppStateStatus } from 'react-native';

import Animated, { useSharedValue, useAnimatedScrollHandler } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import moment from 'moment';
import { appTheme, useThemeColors, type ThemeColors } from '../shared/theme';
import { hexToRgba } from '../shared/theme/colorUtils';
import { useTranslation, getLocale, getSubscriptionPlanTitleFromMerged } from '../shared/i18n';
import { useReportSplashPageReady } from '../lib/useReportSplashPageReady';
import { formatTodayForApi } from '../shared/utils/dateUtils';
import { useRtl } from '../shared/i18n/useRtl';
import { useAuthStore } from '../shared/auth/store';
import { useSubscriptionContext } from '../data/domains/subscription';
import { useOrders } from '../data/domains/orders';
import { useLoyalty } from '../data/domains/loyalty';
import {
  useActiveMenuState,
  useActiveMenuForDateQuery,
  useActiveMenuStoreSelector,
  syncActiveMenuCacheToStore,
  isMenuDateSlotDivergentFromServer,
  mergeMenuBuildersBox1View,
  computeMacrosFromBuilders,
  activeMenuDayKey,
  activeFetchMenu,
} from '../data/domains/activeMenu';
import { MENU_CACHE_STALE_MS, MENU_CACHE_GC_MS } from '../data/domains/menu';
import { useQueryClient } from '@tanstack/react-query';
import { invalidateSubscriptionAndCalendarQueries } from '../data/query';
import {
  useDashboardPreferences,
  useMacroHistory,
  useProgressiveMacroWeekData,
  scheduleReminderNotifications,
  DASHBOARD_FIXED_ORDER,
  type DashboardSectionId,
} from '../data/domains/dashboard';
import { usePosts as useCommunityPosts } from '../data/domains/community';
import { useOrderMenuQuery } from '../data/domains/orders';
import { notificationsQueryKey, useNotifications } from '../data/domains/notifications';
import { addressesQueryKey } from '../data/domains/addresses';
import { DashboardQuickActions } from './components/DashboardQuickActions';
import { DashboardNutritionCard } from './components/DashboardNutritionCard';
import { DashboardWeeklySection } from './components/DashboardWeeklySection';
import { DashboardReminders } from './components/DashboardReminders';
import { AnimatedSection } from '../components/common/AnimatedSection';
import { HUB_SECTION_PROPS } from '../shared/theme/studio/studioMotion';
import { DashboardStickyHeader } from './components/DashboardStickyHeader';
import { DashboardSubscriptionCard } from './components/DashboardSubscriptionCard';
import { resolveV3GreetingName } from '../data/domains/profile';
import { getMenuImagesStripItems, getRandomMenuImageItems, MenuImageTile, computeMenuImageTileMetrics } from './components/DashboardMenuImagesStrip';
import { OnlineOrderImagesStrip } from './components/OnlineOrderImagesStrip';
import { DashboardSelectedMenuCardSet2 } from './components/variants/set2/DashboardSelectedMenuCardSet2';
import { DashboardScrollableGridCard } from './components/DashboardScrollableGridCard';
import { CommunityPostTile, DashboardCommunityStrip } from './components/DashboardCommunityStrip';
import { DashboardClickBannerSet2 } from './components/variants/set2/DashboardClickBannerSet2';
import { DashboardAddressSetupSet2 } from './components/variants/set2/DashboardAddressSetupSet2';
import { DashboardCardShellSet2 } from './components/variants/set2/DashboardCardShellSet2';
import { sharedStyles, androidHeaderTopInset } from '../shared/theme/sharedStyles';
import { ContentCard } from '../components/common/ContentCard';
import { Clickable } from '../components/common/Clickable';
import { DashboardSet1EmptyState } from './components/variants/set1/DashboardSet1EmptyState';
import { dashboardSet1SectionStyles } from './components/variants/set1/dashboardSet1Styles';
import {
  DASH_STUDIO_INNER,
  DASH_STUDIO_RADIUS,
  studioCardShell,
  DASHBOARD_HEADER_EXPANDED_HEIGHT,
  DASHBOARD_PADDING_H,
  DASHBOARD_SECTION_GAP,
  DASHBOARD_COMMUNITY_GRID_MAX_HEIGHT,
} from '../shared/theme/studio/dashboardConstants';
import { useCommunityFeatureEnabled, useConsultationsFeatureEnabled, useJourneyFeatureEnabled, useLoyaltyFeatureEnabled, useOnlineOrdersFeatureEnabled, useDashboardVariant } from '../data/domains/config';
import { SvgIcon } from '../components/common/SvgIcon';
import { SCROLL_VIEW_PROPS } from '../shared/utils/scrollListPerf';
import { ScreenShell } from '../shared/theme/studio/stackScreenShell';

const STRIP_TILE_STYLE = { marginRight: 0 } as const;

export function DashboardScreen() {
  useReportSplashPageReady(true);
  const insets = useSafeAreaInsets();
  const colors = useThemeColors() ?? appTheme.colors;
  const rtl = useRtl();
  const { t } = useTranslation();
  const locale = getLocale();
  const navigation = useAuthNavigation();
  const user = useAuthStore(s => s.user);
  /** Warm notifications cache + unread_count for inbox; dashboard UI unchanged (no header badge). */
  useNotifications({ refetchWhenNotificationsScreenFocused: false });
  const text_align: 'left' | 'right' = rtl.isRTL ? 'right' : 'left';

  const { activeSubscriptions, availableDates, isLoading: subscription_loading, refetch: refetch_subscription, getDayConfig, dayConfigMap, config: date_service_config } = useSubscriptionContext();
  const { refetch: refetch_orders } = useOrders();
  const { refetch: refetch_loyalty } = useLoyalty();
  const { macroTotals, selectedDate: menu_selected_date, isEmpty: menu_is_empty } = useActiveMenuState();

  const today = formatTodayForApi();
  const first_available_date = (availableDates && availableDates.length > 0 ? availableDates[0] : today) ?? today;
  const query_client = useQueryClient();
  const customer_id = useAuthStore(s => s.user?.id);
  const { data: today_menu_data } = useActiveMenuForDateQuery(today, false);
  const dashboard_variant = useDashboardVariant();
  const is_set2 = dashboard_variant === 'set2';
  const need_first_available_menu = is_set2 && first_available_date !== today;
  const { data: first_available_menu_data } = useActiveMenuForDateQuery(first_available_date, false, {
    query_enabled: need_first_available_menu,
  });
  const today_slot = useActiveMenuStoreSelector(s => s.menuStateByDate[today]);
  const today_server_snap = useActiveMenuStoreSelector(s => s.serverSnapshotByDate[today]);
  const today_dirty = useMemo(
    () => isMenuDateSlotDivergentFromServer(today_slot, today_server_snap),
    [today_slot, today_server_snap],
  );
  const today_builders = useMemo(
    () => mergeMenuBuildersBox1View(today_slot, today_menu_data?.menuBuilders, today_dirty),
    [today_slot, today_menu_data?.menuBuilders, today_dirty],
  );

  const day_config_map_ref = useRef(dayConfigMap ?? {});
  day_config_map_ref.current = dayConfigMap ?? {};

  useEffect(() => {
    const from_query = today_menu_data?.menuBuilders ?? [];
    if (from_query.length > 0 && typeof customer_id === 'number') {
      syncActiveMenuCacheToStore(query_client, customer_id, today, day_config_map_ref.current);
    }
  }, [today_menu_data?.menuBuilders, customer_id, today, query_client]);

  const refetch_today_menu = useCallback(async () => {
    if (typeof customer_id !== 'number') {
      return;
    }
    const key = activeMenuDayKey(customer_id, today, false);
    await query_client.fetchQuery({
      queryKey: key,
      queryFn: () => activeFetchMenu(customer_id, today, false),
      staleTime: MENU_CACHE_STALE_MS,
      gcTime: MENU_CACHE_GC_MS,
    });
    syncActiveMenuCacheToStore(query_client, customer_id, today, day_config_map_ref.current);
  }, [query_client, customer_id, today]);

  const menu_strip_items = useMemo(() => getMenuImagesStripItems(today_builders), [today_builders, locale]);
  const first_available_builders = first_available_menu_data?.menuBuilders ?? [];
  const fallback_menu_strip_items = useMemo(
    () => (is_set2 ? getRandomMenuImageItems(first_available_builders, 6) : []),
    [is_set2, first_available_builders, locale],
  );
  const { width: screen_width } = useWindowDimensions();
  const menu_tile_metrics = useMemo(() => computeMenuImageTileMetrics(screen_width), [screen_width]);
  const menu_strip_max_height = menu_tile_metrics.tile_height + appTheme.card.padding.lg * 2 + 12;

  const today_day_config = getDayConfig(today) ?? null;
  const active_sub = activeSubscriptions[0] ?? null;
  const today_macro_totals = useMemo(() => {
    const computed = computeMacrosFromBuilders(today_builders);
    if (computed) {
      return computed;
    }
    return { calories: 0, protein: 0, carbs: 0, fats: 0 };
  }, [today_builders]);
  const today_nutrition_empty =
    today_builders.length === 0 ||
    (today_macro_totals.calories === 0 &&
      today_macro_totals.protein === 0 &&
      today_macro_totals.carbs === 0 &&
      today_macro_totals.fats === 0);

  const scroll_y = useSharedValue(0);
  const scroll_handler = useAnimatedScrollHandler({
    onScroll: e => {
      scroll_y.value = e.contentOffset.y;
    },
  });

  const { prefs, reminders, add_reminder, add_custom_reminder, remove_reminder, toggle_reminder } = useDashboardPreferences();
  const { save_day: save_macro_day } = useMacroHistory();
  const { data: macro_week_data, loaded: macro_week_loaded, refetch: refetch_macro_week } = useProgressiveMacroWeekData({
    enable_fetch: true,
  });
  const { data: community_posts, refetch: refetch_community } = useCommunityPosts();
  const { data: order_menu_data, isLoading: order_menu_loading, refetch: refetch_order_menu } = useOrderMenuQuery();
  /** From GET app_config (`show_community` only). */
  const show_community_section = useCommunityFeatureEnabled();
  const show_consultations_action = useConsultationsFeatureEnabled();
  const show_journey_action = useJourneyFeatureEnabled();
  const show_loyalty_action = useLoyaltyFeatureEnabled();
  const show_online_orders_feature = useOnlineOrdersFeatureEnabled();

  const week_has_any_macros = useMemo(
    () =>
      macro_week_data.some(
        d =>
          (d.calories ?? 0) > 0 || (d.protein ?? 0) > 0 || (d.carbs ?? 0) > 0 || (d.fats ?? 0) > 0,
      ),
    [macro_week_data],
  );

  /** Always show today's macros (empty state prompts to select meals). set2 omits the macro card (v2 layout). */
  const show_daily_macros = !is_set2;
  const is_set1 = dashboard_variant === 'set1';
  const section_gap = is_set1 ? appTheme.spacing.md : DASHBOARD_SECTION_GAP;
  /** set2 (v2) has no macro charts; set1 / set3 use week intake charts. */
  const show_weekly_section =
    is_set1 || (dashboard_variant === 'set3' && macro_week_loaded && week_has_any_macros);

  const go_menu_flow = useCallback(() => navigation.navigate('DailyMenuFlow'), [navigation]);
  const today_macros_date_label = useMemo(() => {
    try {
      const loc = getLocale();
      moment.locale(loc === 'ar' ? 'ar' : 'en');
      return moment(today, 'YYYY-MM-DD').format('MMM D');
    } catch {
      moment.locale('en');
      return moment(today, 'YYYY-MM-DD').format('MMM D');
    }
  }, [today]);
  const order_catalog_count = order_menu_data?.allItems?.length ?? 0;
  const show_online_order_strip =
    show_online_orders_feature && (order_menu_loading || order_catalog_count > 0);
  const go_community = useCallback(() => navigation.navigate('Community'), [navigation]);
  const go_subscriptions = useCallback(
    () => navigation.navigate(active_sub ? 'MySubscriptions' : 'SubscriptionList'),
    [navigation, active_sub],
  );

  const menu_image_tiles = useMemo(
    () =>
      menu_strip_items.map(item => (
        <MenuImageTile key={item.key} item={item} colors={colors} style={STRIP_TILE_STYLE} tile_metrics={menu_tile_metrics} />
      )),
    [menu_strip_items, colors, menu_tile_metrics],
  );

  const community_post_tiles = useMemo(
    () =>
      community_posts.map(post => (
        <CommunityPostTile key={post.id} post={post} colors={colors} style={STRIP_TILE_STYLE} onPress={go_community} />
      )),
    [community_posts, colors, go_community],
  );

  const [refreshing, set_refreshing] = useState(false);

  useEffect(() => {
    if (menu_selected_date && !menu_is_empty && (macroTotals.calories > 0 || macroTotals.protein > 0 || macroTotals.carbs > 0 || macroTotals.fats > 0)) {
      save_macro_day(menu_selected_date, macroTotals.calories, macroTotals.protein, macroTotals.carbs, macroTotals.fats);
    }
  }, [menu_selected_date, menu_is_empty, macroTotals, save_macro_day]);

  useEffect(() => {
    if (reminders.length === 0) {return;}
    scheduleReminderNotifications(reminders).catch(() => {});
  }, [reminders]);

  useEffect(() => {
    const sub = AppState.addEventListener('change', (state: AppStateStatus) => {
      if (state === 'active' && reminders.length > 0) {
        scheduleReminderNotifications(reminders).catch(() => {});
      }
    });
    return () => sub.remove();
  }, [reminders]);

  const on_refresh = useCallback(async () => {
    set_refreshing(true);
    refetch_macro_week();
    if (typeof customer_id === 'number') {
      query_client.invalidateQueries({ queryKey: notificationsQueryKey(customer_id) });
      if (is_set2) {
        query_client.invalidateQueries({ queryKey: addressesQueryKey });
      }
    }
    const sub_cal =
      typeof customer_id === 'number'
        ? invalidateSubscriptionAndCalendarQueries(query_client, customer_id)
        : refetch_subscription();
    await Promise.all([
      sub_cal,
      refetch_orders(),
      refetch_loyalty(),
      refetch_today_menu(),
      refetch_community(),
      refetch_order_menu(),
    ]);
    set_refreshing(false);
  }, [
    customer_id,
    query_client,
    refetch_subscription,
    refetch_orders,
    refetch_loyalty,
    refetch_today_menu,
    refetch_community,
    refetch_order_menu,
    refetch_macro_week,
    is_set2,
  ]);

  /** Clear full sticky header (safe-area + expanded band) in the unscrolled state. */
  const header_clearance = androidHeaderTopInset(insets.top) + DASHBOARD_HEADER_EXPANDED_HEIGHT;
  const content_top = header_clearance;
  /** Pull-to-refresh sits in the rhythm gap below the header (not under the frosted bar). */
  const refresh_progress_offset = header_clearance + Math.round(DASHBOARD_SECTION_GAP / 2);
  const padding_bottom = insets.bottom + appTheme.spacing.xl;

  const section_content = useMemo((): Record<DashboardSectionId, React.ReactNode> => ({
    subscription: (
      <AnimatedSection {...HUB_SECTION_PROPS} index={0}>
        <View style={[styles.dashboardSection, { marginBottom: section_gap }]}>
          <DashboardSubscriptionCard
            colors={colors}
            subscription={active_sub}
            available_count={availableDates.length}
            available_dates={availableDates}
            active_subscriptions={activeSubscriptions}
            day_config_map={dayConfigMap}
            date_service_config={date_service_config}
            subscription_loading={subscription_loading}
            on_press={go_subscriptions}
            rtl={rtl}
            menu_fallback_items={menu_strip_items}
          />
        </View>
      </AnimatedSection>
    ),
    rewardsBanner: !is_set2 ? null : (
      <AnimatedSection {...HUB_SECTION_PROPS} index={0}>
        <View style={[styles.dashboardSection, { marginBottom: section_gap }]}>
          <DashboardCardShellSet2 colors={colors} no_padding>
            <DashboardClickBannerSet2
              today_menu_strip_items={menu_strip_items}
              fallback_menu_strip_items={fallback_menu_strip_items}
            />
          </DashboardCardShellSet2>
        </View>
      </AnimatedSection>
    ),
    onlineOrderImages: !show_online_order_strip ? null : (
      <AnimatedSection {...HUB_SECTION_PROPS} index={0}>
        <View style={[styles.dashboardSection, { marginBottom: section_gap }]}>
          <OnlineOrderImagesStrip colors={colors} />
        </View>
      </AnimatedSection>
    ),
    dailyMacros: !show_daily_macros ? null : (
      <AnimatedSection {...HUB_SECTION_PROPS} index={0}>
        <View style={[styles.dashboardSection, { marginBottom: section_gap }]}>
          <DashboardNutritionCard
            macro_totals={today_macro_totals}
            day_config={today_day_config}
            selected_date={today}
            colors={colors}
            is_empty={today_nutrition_empty}
          />
        </View>
      </AnimatedSection>
    ),
    weeklyMacros: !show_weekly_section ? null : (
      <AnimatedSection {...HUB_SECTION_PROPS} index={0}>
        <View style={[styles.dashboardSection, is_set1 ? dashboardSet1SectionStyles.tertiarySection : null, { marginBottom: section_gap }]}>
          <DashboardWeeklySection colors={colors} on_browse_plans={go_subscriptions} />
        </View>
      </AnimatedSection>
    ),
    selectedMeals: (
      <AnimatedSection {...HUB_SECTION_PROPS} index={0}>
        <View style={[styles.dashboardSection, is_set1 ? dashboardSet1SectionStyles.secondarySection : null, { marginBottom: section_gap }]}>
          {menu_strip_items.length > 0 ? (
            is_set2 ? (
              <DashboardSelectedMenuCardSet2
                colors={colors}
                items={menu_strip_items}
                title={t('dashboard.selectedMenuToday')}
                on_press={go_menu_flow}
                tile_metrics={menu_tile_metrics}
              />
            ) : (
              <DashboardScrollableGridCard
                colors={colors}
                title={is_set1 ? t('dashboard.sectionYourMenu') : t('dashboard.selectedMenuToday')}
                on_container_press={go_menu_flow}
                items={menu_image_tiles}
                num_columns={999}
                max_height={menu_strip_max_height}
                item_gap={appTheme.spacing.sm}
                key_extractor={i => menu_strip_items[i]?.key ?? `grid-${i}`}
              />
            )
          ) : is_set1 ? (
            <DashboardSet1EmptyState
              colors={colors}
              icon="menu"
              title={t('dashboard.sectionYourMenu')}
              message={t('dashboard.selectMealsHint').replace('{{date}}', today_macros_date_label)}
              cta_label={t('dashboard.exploreDishes')}
              on_cta={go_menu_flow}
            />
          ) : (
            <ContentCard
              variant="outlined"
              borderless
              style={[styles.studioEmptyCard, studioCardShell(colors), { backgroundColor: colors.surface }]}
              padding="lg">
              <Text
                style={[
                  styles.emptySectionTitle,
                  { color: colors.textSecondary, textAlign: text_align, writingDirection: rtl.isRTL ? 'rtl' : 'ltr' },
                ]}
                numberOfLines={2}>
                {t('dashboard.selectedMenuToday')}
              </Text>
              <Text
                style={[
                  styles.emptySectionBody,
                  { color: colors.textSecondary, textAlign: text_align, writingDirection: rtl.isRTL ? 'rtl' : 'ltr' },
                ]}>
                {t('dashboard.selectMealsHint').replace('{{date}}', today_macros_date_label)}
              </Text>
              <Clickable
                onPress={go_menu_flow}
                style={[styles.emptySectionCta, { backgroundColor: colors.primary }]}
                accessibilityRole="button"
                accessibilityLabel={t('dashboard.exploreDishes')}>
                <Text style={[styles.emptySectionCtaText, { color: colors.onPrimary ?? colors.white }]}>{t('dashboard.exploreDishes')}</Text>
              </Clickable>
            </ContentCard>
          )}
        </View>
      </AnimatedSection>
    ),
    communityPosts: !show_community_section || is_set2 ? null : community_posts.length > 0 ? (
      <AnimatedSection {...HUB_SECTION_PROPS} index={0}>
        <View style={[styles.dashboardSection, { marginBottom: section_gap }]}>
          <DashboardScrollableGridCard
            colors={colors}
            title={t('dashboard.viewLatestCommunityPosts')}
            on_container_press={go_community}
            items={community_post_tiles}
            num_columns={999}
            max_height={DASHBOARD_COMMUNITY_GRID_MAX_HEIGHT}
            item_gap={appTheme.spacing.md}
            key_extractor={i => String(community_posts[i]?.id ?? `community-${i}`)}
            content_container_style={styles.communityGridScrollContent}
          />
        </View>
      </AnimatedSection>
    ) : (
      <AnimatedSection {...HUB_SECTION_PROPS} index={0}>
        <View style={[styles.dashboardSection, is_set1 ? dashboardSet1SectionStyles.tertiarySection : null, { marginBottom: section_gap }]}>
          {is_set1 ? (
            <DashboardSet1EmptyState
              colors={colors}
              icon="chat"
              title={t('community.title')}
              message={t('community.noPostsTapToOpenCommunity')}
              cta_label={t('community.openCommunity')}
              on_cta={go_community}
            />
          ) : (
            <DashboardCommunityStrip posts={[]} colors={colors} />
          )}
        </View>
      </AnimatedSection>
    ),
    addressSetup: !is_set2 ? null : (
      <AnimatedSection {...HUB_SECTION_PROPS} index={0}>
        <View style={[styles.dashboardSection, { marginBottom: section_gap }]}>
          <DashboardAddressSetupSet2 colors={colors} />
        </View>
      </AnimatedSection>
    ),
    reminders: (
      <AnimatedSection {...HUB_SECTION_PROPS} index={0}>
        <View style={[styles.dashboardSection, { marginBottom: section_gap }]}>
          <DashboardReminders
            colors={colors}
            reminders={reminders}
            on_add={add_reminder}
            on_add_custom={add_custom_reminder}
            on_remove={remove_reminder}
            on_toggle={toggle_reminder}
          />
        </View>
      </AnimatedSection>
    ),
    quickActions: (
      <AnimatedSection {...HUB_SECTION_PROPS} index={0}>
        <View style={[styles.dashboardSection, is_set1 ? dashboardSet1SectionStyles.tertiarySection : null, { marginBottom: section_gap }]}>
          <DashboardQuickActions
            colors={colors}
            show_community_action={show_community_section}
            show_consultations_action={show_consultations_action}
            show_journey_action={show_journey_action}
            show_loyalty_action={show_loyalty_action}
          />
        </View>
      </AnimatedSection>
    ),
  }), [
    colors,
    active_sub,
    availableDates,
    activeSubscriptions,
    dayConfigMap,
    date_service_config,
    subscription_loading,
    go_subscriptions,
    text_align,
    rtl,
    dashboard_variant,
    section_gap,
    show_online_order_strip,
    show_daily_macros,
    today_macro_totals,
    today_day_config,
    today,
    today_nutrition_empty,
    show_weekly_section,
    macro_week_loaded,
    week_has_any_macros,
    go_subscriptions,
    menu_strip_items,
    menu_image_tiles,
    t,
    go_menu_flow,
    is_set1,
    today_macros_date_label,
    show_community_section,
    community_posts,
    community_post_tiles,
    go_community,
    reminders,
    add_reminder,
    add_custom_reminder,
    remove_reminder,
    toggle_reminder,
    show_consultations_action,
    show_journey_action,
    show_loyalty_action,
    is_set2,
    fallback_menu_strip_items,
  ]);

  /** set2 (v2) layout: banner + address grouped near the top, selected-meals strip before reminders. */
  const render_order: readonly DashboardSectionId[] = is_set2
    ? [
        'subscription',
        'rewardsBanner',
        'onlineOrderImages',
        'addressSetup',
        'selectedMeals',
        'reminders',
        'quickActions',
      ]
    : DASHBOARD_FIXED_ORDER;

  const visible_section_order = render_order.filter(id => section_content[id] != null);

  return (
    <ScreenShell colors={colors}>
    <View style={[sharedStyles.container, styles.container, { backgroundColor: colors.background }]}>
      <DashboardStickyHeader colors={colors} user_name={resolveV3GreetingName(user)} scroll_y={scroll_y} top_inset={insets.top} />

      <Animated.ScrollView
        {...SCROLL_VIEW_PROPS}
        style={[styles.scroll, { zIndex: 1 }]}
        contentContainerStyle={[
          styles.scrollContent,
          {
            paddingTop: content_top,
            paddingBottom: padding_bottom,
            paddingHorizontal: 0,
          },
        ]}
        onScroll={scroll_handler}
        scrollEventThrottle={16}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={on_refresh}
            tintColor={colors.primary}
            colors={[colors.primary]}
            progressBackgroundColor={colors.surface}
            progressViewOffset={refresh_progress_offset}
          />
        }>
        {render_order.map(id => {
          const node = section_content[id];
          if (node == null) {
            return null;
          }
          const anim_index = visible_section_order.indexOf(id);
          const stagger_index = anim_index >= 0 ? anim_index : 0;
          const section_wrap_style = stagger_index === 0 ? [styles.dashboardSectionFirst, { marginTop: section_gap }] : undefined;
          if (isValidElement(node)) {
            return (
              <View key={id} style={section_wrap_style}>{cloneElement(node, { index: stagger_index } as { index: number })}</View>
            );
          }
          return <View key={id} style={section_wrap_style}>{node}</View>;
        })}
        <View style={{ height: appTheme.spacing.xxl }} />
      </Animated.ScrollView>
    </View>
    </ScreenShell>
  );
}

export default DashboardScreen;

const styles = StyleSheet.create({
  container: {
    paddingBottom: appTheme.spacing.xxl,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  /** Single horizontal gutter for all blocks; bottom margin matches header→first-card rhythm. */
  dashboardSection: {
    paddingHorizontal: DASHBOARD_PADDING_H,
    marginBottom: DASHBOARD_SECTION_GAP,
  },
  dashboardSectionFirst: {
    marginTop: DASHBOARD_SECTION_GAP,
  },
  emptySectionTitle: {
    fontSize: 14,
    fontWeight: '800' as const,
    letterSpacing: 0.3,
    marginBottom: appTheme.spacing.sm,
  },
  emptySectionBody: {
    ...appTheme.typography.bodySmall,
    fontSize: 13,
    lineHeight: 20,
    marginBottom: appTheme.spacing.md,
  },
  emptySectionCta: {
    alignSelf: 'flex-start',
    paddingVertical: appTheme.spacing.sm,
    paddingHorizontal: appTheme.spacing.lg,
    borderRadius: appTheme.card.radius,
  },
  emptySectionCtaText: {
    fontSize: 14,
    fontWeight: '700',
  },
  newBlockPlaceholder: {
    minHeight: 80,
  },
  communityGridScrollContent: {
    minHeight: 228,
  },
  studioEmptyCard: {
    borderRadius: DASH_STUDIO_RADIUS,
  },
});
